import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock as LockIcon, Mail as MailIcon, Save as SaveIcon, Plus as PlusIcon, Trash2 as TrashIcon, Loader2 as SpinIcon, ArrowUp as UpIcon, ArrowDown as DownIcon, Flag as FlagIcon } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useStatuses } from "@/context/StatusContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const LANGS = ["en", "hu", "de", "fr", "cs"];
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

export function ModStatuses() {
  const { statuses, reload } = useStatuses();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(
      statuses.map((s) => ({
        key: s.key,
        labels: { en: "", hu: "", de: "", fr: "", ...(s.labels || {}) },
        color: s.color || "#8b5cf6",
        builtin: !!s.builtin,
        sends_email: !!s.sends_email,
        closes_case: !!s.closes_case,
      }))
    );
  }, [statuses]);

  const update = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const updateLabel = (i, lang, v) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, labels: { ...row.labels, [lang]: v } } : row)));
  const remove = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const move = (i, dir) =>
    setRows((r) => {
      const j = i + dir;
      if (j < 0 || j >= r.length) return r;
      const copy = [...r];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  const add = () =>
    setRows((r) => [
      ...r,
      { key: "", labels: { en: "", hu: "", de: "", fr: "" }, color: "#8b5cf6", builtin: false, sends_email: false, closes_case: false },
    ]);

  const save = async () => {
    const items = rows.map((row) => ({
      key: row.builtin ? row.key : row.key || slugify(row.labels.en),
      labels: row.labels,
      color: row.color,
      sends_email: row.sends_email,
      closes_case: row.closes_case,
    }));
    if (items.some((it) => !it.labels.en?.trim())) {
      toast.error("Minden státuszhoz kötelező az angol (EN) címke");
      return;
    }
    if (items.some((it) => !it.key)) {
      toast.error("Minden státuszhoz kell egy kulcs (az EN címkéből generáljuk)");
      return;
    }
    setSaving(true);
    try {
      await api.put("/mod/statuses", { items });
      await reload();
      toast.success("Státuszok mentve");
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="mod-statuses">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-zinc-50">Bejelentés-státuszok</h2>
          <p className="text-[12px] text-zinc-500">
            Az 5 beépített státusz védett (nem törölhető, a viselkedés-kapcsolóik rögzítettek). Vegyél fel saját státuszokat egyedi színnel és viselkedéssel.
          </p>
        </div>
        <Button data-testid="statuses-add" onClick={add} variant="outline" className="h-9 gap-1.5 border-zinc-800 bg-zinc-900 text-[13px] text-zinc-200 hover:bg-zinc-800">
          <PlusIcon size={15} /> Új státusz
        </Button>
      </div>

      <div className="space-y-2.5">
        {rows.map((row, i) => (
          <div key={i} data-testid={`status-row-${i}`} className="rounded-xl border border-zinc-800/70 bg-[#111113] p-3.5">
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="color"
                data-testid={`status-color-${i}`}
                value={row.color}
                onChange={(e) => update(i, { color: e.target.value })}
                className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-zinc-800 bg-zinc-950 p-0.5"
                title="Szín"
              />
              <span
                className="inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-medium"
                style={{ color: row.color, borderColor: `${row.color}66`, backgroundColor: `${row.color}1a` }}
              >
                {row.labels.hu || row.labels.en || "Új státusz"}
              </span>
              {row.builtin ? (
                <span className="inline-flex items-center gap-1 rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">
                  <LockIcon size={11} /> beépített
                </span>
              ) : (
                <span className="font-mono text-[11px] text-zinc-600">{row.key || slugify(row.labels.en) || "—"}</span>
              )}

              <div className="ml-auto flex items-center gap-1">
                <button data-testid={`status-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="grid h-7 w-7 place-items-center rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-30">
                  <UpIcon size={14} />
                </button>
                <button data-testid={`status-down-${i}`} onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="grid h-7 w-7 place-items-center rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-30">
                  <DownIcon size={14} />
                </button>
                {!row.builtin && (
                  <button data-testid={`status-delete-${i}`} onClick={() => remove(i)} className="grid h-7 w-7 place-items-center rounded-md text-red-400/70 hover:bg-red-950/40 hover:text-red-300">
                    <TrashIcon size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {LANGS.map((l) => (
                <div key={l}>
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">{l}{l === "en" ? " *" : ""}</div>
                  <Input
                    data-testid={`status-label-${i}-${l}`}
                    value={row.labels[l] || ""}
                    onChange={(e) => updateLabel(i, l, e.target.value)}
                    placeholder={l.toUpperCase()}
                    className="h-8 border-zinc-800 bg-zinc-950 text-[12px] text-zinc-100"
                  />
                </div>
              ))}
            </div>

            <div className="mt-3 flex flex-wrap gap-5 border-t border-zinc-800/60 pt-3">
              <label className="flex items-center gap-2 text-[12px] text-zinc-300">
                <Switch
                  data-testid={`status-email-${i}`}
                  checked={row.sends_email}
                  onCheckedChange={(v) => update(i, { sends_email: v })}
                  disabled={row.builtin}
                />
                <span className="inline-flex items-center gap-1"><MailIcon size={13} className="text-zinc-500" /> Emailt küld a bejelentőnek</span>
              </label>
              <label className="flex items-center gap-2 text-[12px] text-zinc-300">
                <Switch
                  data-testid={`status-close-${i}`}
                  checked={row.closes_case}
                  onCheckedChange={(v) => update(i, { closes_case: v })}
                  disabled={row.builtin}
                />
                <span className="inline-flex items-center gap-1"><FlagIcon size={13} className="text-zinc-500" /> Lezárt ügy (SLA leáll)</span>
              </label>
              {row.builtin && <span className="text-[11px] text-zinc-600">A beépített kapcsolók rögzítettek.</span>}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex justify-end">
        <Button data-testid="statuses-save" onClick={save} disabled={saving} className="h-10 gap-2 bg-blue-600 px-6 text-[13px] font-semibold text-white hover:bg-blue-500">
          {saving ? <SpinIcon size={16} className="animate-spin" /> : <SaveIcon size={16} />} Mentés
        </Button>
      </div>
    </div>
  );
}
